import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  Check,
  Eye,
  EyeOff,
  KeyRound,
  Laptop,
  LogOut,
  Monitor,
  Moon,
  Save,
  ShieldAlert,
  Smartphone,
  Sun,
  X,
  Languages,
} from 'lucide-react';
import apiClient from '../api/client';
import { useAuthStore } from '../stores/authStore';
import { useCapabilitiesStore } from '../stores/capabilitiesStore';
import { useThemeStore, type Theme } from '../stores/themeStore';
import { useLocaleStore } from '../stores/localeStore';
import { LOCALE_LABELS, LOCALES } from '../i18n';
import { useToast } from '../components/ui/Toast';
import { useConfirm } from '../components/ui/ConfirmDialog';
import { PageHeader } from '../components/ui/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Skeleton } from '../components/ui/Skeleton';
import { cn } from '../utils/cn';
import type { TFunction } from 'i18next';
import type { Role } from '../auth/roles';

interface Profile {
  id: string;
  email: string;
  fullName: string | null;
  role: Role;
  customerId: string | null;
  customerName: string | null;
  active: boolean;
  mustChangePassword: boolean;
  createdAt: string;
  lastSignInAt: string | null;
  failedAttemptsSinceSuccess: number;
  lockedUntil: string | null;
  maxDiscountPercent: number | null;
}

interface Session {
  jti: string;
  issuedAt: string;
  expiresAt: string;
  userAgent: string | null;
  ip: string | null;
}

interface SignIn {
  at: string;
  success: boolean;
  ip: string | null;
}

interface SessionResponse {
  accessToken: string;
  refreshToken: string;
  user: {
    id: string;
    email: string;
    role: Role;
    customerId: string | null;
    name: string | null;
    mustChangePassword: boolean;
  };
}

// Dates stay in French format whatever the interface language: the business, its opening hours
// and its documents are French, so 04/08/2026 is the reading everyone here shares.
const dt = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

const day = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString('fr-FR', { dateStyle: 'long' }) : '—';

/** Initials for the avatar tile — from the name when we have one, else the email. */
function initials(name: string | null, email: string): string {
  const source = (name ?? email.split('@')[0]).trim();
  const parts = source.split(/[\s._-]+/).filter(Boolean);
  return (
    ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || source[0]?.toUpperCase() || '?'
  );
}

/**
 * The stored User-Agent, read back as something a person recognises. Deliberately crude — this
 * label only has to help someone answer "is one of these not me?", so a wrong guess on an exotic
 * browser costs nothing, and we never want a UA-parsing dependency for a single line of text.
 */
function describeDevice(
  ua: string | null,
  t: TFunction
): { label: string; kind: 'mobile' | 'desktop' } {
  if (!ua) return { label: t('profile.devices.unknownDevice'), kind: 'desktop' };
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /OPR\/|Opera/.test(ua)
      ? 'Opera'
      : /Chrome\//.test(ua)
        ? 'Chrome'
        : /Firefox\//.test(ua)
          ? 'Firefox'
          : /Safari\//.test(ua)
            ? 'Safari'
            : t('profile.devices.genericBrowser');
  const os = /Windows/.test(ua)
    ? 'Windows'
    : /Android/.test(ua)
      ? 'Android'
      : /iPhone|iPad|iPod/.test(ua)
        ? 'iOS'
        : /Mac OS X|Macintosh/.test(ua)
          ? 'macOS'
          : /Linux/.test(ua)
            ? 'Linux'
            : t('profile.devices.unknownOs');
  const mobile = /Mobile|Android|iPhone|iPad|iPod/.test(ua);
  return {
    label: t('profile.devices.onOs', { browser, os }),
    kind: mobile ? 'mobile' : 'desktop',
  };
}

/**
 * The password policy the API enforces (isPasswordStrong, backend/src/auth/password.ts). Kept in
 * the same shape as the server's rule set so the checklist below and the 422 the server would
 * return can never disagree — the server remains the only enforcement.
 */
const RULES: ReadonlyArray<{ key: string; ok: (p: string) => boolean }> = [
  { key: 'length', ok: (p) => p.length >= 8 },
  { key: 'uppercase', ok: (p) => /[A-Z]/.test(p) },
  { key: 'digit', ok: (p) => /[0-9]/.test(p) },
  { key: 'special', ok: (p) => /[^A-Za-z0-9]/.test(p) },
];

const THEMES: ReadonlyArray<{ value: Theme; icon: typeof Sun }> = [
  { value: 'light', icon: Sun },
  { value: 'dark', icon: Moon },
  { value: 'system', icon: Monitor },
];

/** The jti of the refresh token this browser holds — how we tell "this device" apart in the list. */
const ownJti = (): string | null => localStorage.getItem('refresh_token')?.split('.')[0] ?? null;

/** Devices shown per page. A counter PC signed in from a dozen places should not push the
 *  capabilities card off the screen, and five rows is about a glance's worth. */
const SESSIONS_PER_PAGE = 5;

/**
 * Screens 3-5 — the signed-in user's own account (/profile).
 *
 * Everything here is self-service and hits the `/me/*` routes, which scope to the JWT's user id:
 * display name, password, live sessions, recent sign-in attempts, appearance. What is NOT here is
 * as deliberate as what is: role, email and account status are admin-owned (/admin/users), because
 * a page where you can edit your own role is a privilege-escalation button.
 */
export default function ProfilePage() {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const [confirm, confirmDialog] = useConfirm();
  const setSession = useAuthStore((s) => s.setSession);
  const caps = useCapabilitiesStore((s) => s.data);
  const fetchCaps = useCapabilitiesStore((s) => s.fetch);
  const theme = useThemeStore((s) => s.theme);
  const setTheme = useThemeStore((s) => s.setTheme);
  const locale = useLocaleStore((s) => s.locale);
  const setLocale = useLocaleStore((s) => s.setLocale);
  const savingLocale = useLocaleStore((s) => s.saving);

  const [profile, setProfile] = useState<Profile | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [signIns, setSignIns] = useState<SignIn[]>([]);
  const [loading, setLoading] = useState(true);

  // Identity
  const [name, setName] = useState('');
  const [savingName, setSavingName] = useState(false);

  // Password
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [reveal, setReveal] = useState(false);
  const [changing, setChanging] = useState(false);

  const [revoking, setRevoking] = useState<string | null>(null);
  const [sessionPage, setSessionPage] = useState(0);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      // One await for the three panels: they are independent reads and the page shows them
      // together, so serialising them would only make the screen slower to settle.
      const [p, s, h] = await Promise.all([
        apiClient.get<{ data: Profile }>('/me/profile'),
        apiClient.get<{ data: Session[] }>('/me/sessions'),
        // 8 barely filled the panel; now that it scrolls, fetch a useful history instead. The
        // endpoint clamps at 50, so this is the shape of request it was built for.
        apiClient.get<{ data: SignIn[] }>('/me/sign-ins?limit=25'),
      ]);
      setProfile(p.data.data);
      setName(p.data.data.fullName ?? '');
      setSessions(s.data.data);
      setSignIns(h.data.data);
    } catch (err) {
      showApiError(err);
    } finally {
      setLoading(false);
    }
  }, [showApiError]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * The jti this browser holds, read once per sessions change rather than on every render: it
   * decides both the "This device" badge and the ordering below, and the API client rotates the
   * stored refresh token in the background, so re-reading mid-render could reorder the list
   * under the user's cursor.
   */
  const currentJti = useMemo(() => ownJti(), [sessions]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * This device first, then the rest newest-first as the API returned them. Without the pin, the
   * one session a user is looking for could sit on page 2 — and "sign out the one that isn't me"
   * starts by finding the one that is.
   */
  const orderedSessions = useMemo(() => {
    const mine = sessions.filter((s) => s.jti === currentJti);
    return [...mine, ...sessions.filter((s) => s.jti !== currentJti)];
  }, [sessions, currentJti]);

  // Revoking the last row of the last page (or a reload returning fewer sessions) would leave the
  // page index past the end, showing an empty list with no way back. Clamp instead.
  useEffect(() => {
    const pages = Math.max(1, Math.ceil(orderedSessions.length / SESSIONS_PER_PAGE));
    if (sessionPage > pages - 1) setSessionPage(pages - 1);
  }, [orderedSessions.length, sessionPage]);

  const nameDirty = (profile?.fullName ?? '') !== name.trim();

  const saveName = async (): Promise<void> => {
    setSavingName(true);
    try {
      const { data } = await apiClient.patch<{ data: Profile }>('/me/profile', {
        fullName: name.trim(),
      });
      setProfile(data.data);
      setName(data.data.fullName ?? '');
      push({ tone: 'success', message: t('profile.account.updated') });
    } catch (err) {
      showApiError(err);
    } finally {
      setSavingName(false);
    }
  };

  /** The switch itself never fails visibly: the language is applied locally either way, and only
   *  the write-back to users.locale can fail — which is what the warning says. */
  const switchLanguage = async (value: (typeof LOCALES)[number]): Promise<void> => {
    try {
      await setLocale(value);
    } catch {
      push({ tone: 'warning', message: t('profile.language.saveFailed') });
    }
  };

  const rulesMet = useMemo(() => RULES.map((r) => r.ok(next)), [next]);
  const strongEnough = rulesMet.every(Boolean);
  const mismatch = confirmPwd.length > 0 && next !== confirmPwd;
  const canSubmitPassword =
    current.length > 0 && strongEnough && next === confirmPwd && confirmPwd.length > 0;

  const changePassword = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!canSubmitPassword) return;
    setChanging(true);
    try {
      const { data } = await apiClient.post<{ data: SessionResponse }>('/me/password', {
        currentPassword: current,
        newPassword: next,
      });
      const r = data.data;
      // The API revokes every other session as part of the change and hands back a fresh pair
      // for THIS device. Store both or the very next request 401s and bounces us to /login.
      localStorage.setItem('refresh_token', r.refreshToken);
      setSession(r.accessToken, {
        sub: r.user.id,
        role: r.user.role,
        customerId: r.user.customerId,
        email: r.user.email,
        mustChangePassword: r.user.mustChangePassword,
      });
      await fetchCaps();
      setCurrent('');
      setNext('');
      setConfirmPwd('');
      push({
        tone: 'success',
        title: t('profile.password.changed'),
        message: t('profile.password.changedDetail'),
      });
      await load();
    } catch (err) {
      showApiError(err);
    } finally {
      setChanging(false);
    }
  };

  const revokeOne = async (s: Session): Promise<void> => {
    const { label } = describeDevice(s.userAgent, t);
    const ok = await confirm({
      title: t('profile.devices.confirmOne'),
      description: t('profile.devices.confirmOneDetail', {
        device: `${label}${s.ip ? ` (${s.ip})` : ''}`,
      }),
      confirmLabel: t('profile.devices.confirmOneAction'),
      tone: 'danger',
    });
    if (!ok) return;
    setRevoking(s.jti);
    try {
      await apiClient.delete(`/me/sessions/${s.jti}`);
      setSessions((rows) => rows.filter((r) => r.jti !== s.jti));
      push({ tone: 'success', message: t('profile.devices.signedOut') });
    } catch (err) {
      showApiError(err);
    } finally {
      setRevoking(null);
    }
  };

  const revokeOthers = async (): Promise<void> => {
    const others = sessions.filter((s) => s.jti !== ownJti()).length;
    const ok = await confirm({
      title: t('profile.devices.confirmOthers', { count: others }),
      description: t('profile.devices.confirmOthersDetail'),
      confirmLabel: t('profile.devices.confirmOthersAction'),
      tone: 'danger',
    });
    if (!ok) return;
    setRevoking('others');
    try {
      const { data } = await apiClient.post<{ data: { revoked: number } }>(
        '/me/sessions/revoke-others',
        { keepJti: ownJti() }
      );
      push({
        tone: 'success',
        message: t('profile.devices.revoked', { count: data.data.revoked }),
      });
      await load();
    } catch (err) {
      showApiError(err);
    } finally {
      setRevoking(null);
    }
  };

  if (loading && !profile) {
    return (
      <div className="p-6">
        <PageHeader title={t('profile.title')} description={t('profile.loadingDescription')} />
        <div className="space-y-4">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (!profile) {
    return <div className="p-6 text-muted-foreground">{t('profile.loadFailed')}</div>;
  }

  const otherSessions = sessions.filter((s) => s.jti !== currentJti).length;
  const totalPages = Math.max(1, Math.ceil(orderedSessions.length / SESSIONS_PER_PAGE));
  const pageSessions = orderedSessions.slice(
    sessionPage * SESSIONS_PER_PAGE,
    (sessionPage + 1) * SESSIONS_PER_PAGE
  );

  return (
    <div>
      <PageHeader title={t('profile.title')} description={t('profile.description')} />

      {profile.failedAttemptsSinceSuccess > 0 && (
        <div className="mb-6 flex items-start gap-3 rounded-lg border border-warning/40 bg-warning/10 p-4 text-sm">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
          <div>
            <p className="font-medium">
              {t('profile.failedAttempts', { count: profile.failedAttemptsSinceSuccess })}
            </p>
            <p className="text-muted-foreground">{t('profile.failedAttemptsHint')}</p>
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* ---------------------------------------------------------------- Identity */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>{t('profile.account.title')}</CardTitle>
            <CardDescription>{t('profile.account.description')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-start gap-4">
              <div className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-primary/10 text-lg font-semibold text-primary">
                {initials(profile.fullName, profile.email)}
              </div>
              <div className="min-w-0 flex-1 space-y-1.5">
                <Label htmlFor="full-name">{t('profile.account.displayName')}</Label>
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    id="full-name"
                    className="max-w-xs"
                    placeholder={profile.email.split('@')[0]}
                    value={name}
                    maxLength={120}
                    onChange={(e) => setName(e.target.value)}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!nameDirty || savingName}
                    onClick={saveName}
                    title={
                      nameDirty ? t('profile.account.saveName') : t('profile.account.nothingToSave')
                    }
                  >
                    <Save className="h-3.5 w-3.5" />
                    {savingName ? t('common.saving') : t('common.save')}
                  </Button>
                  {nameDirty && !savingName && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-muted-foreground"
                      onClick={() => setName(profile.fullName ?? '')}
                    >
                      {t('common.cancel')}
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('profile.account.displayNameHint')}
                </p>
              </div>
            </div>

            <dl className="grid gap-x-8 gap-y-4 border-t pt-5 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                  {t('common.email')}
                </dt>
                <dd className="mt-1 break-all">{profile.email}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                  {t('common.role')}
                </dt>
                <dd className="mt-1 flex items-center gap-2">
                  <Badge variant="secondary">
                    {t(`roles.${profile.role}`, { defaultValue: profile.role })}
                  </Badge>
                  {!profile.active && (
                    <Badge variant="destructive">{t('profile.account.inactive')}</Badge>
                  )}
                  {profile.lockedUntil && new Date(profile.lockedUntil) > new Date() && (
                    <Badge variant="warning">
                      {t('profile.account.lockedUntil', { date: dt(profile.lockedUntil) })}
                    </Badge>
                  )}
                </dd>
              </div>
              {profile.customerId && (
                <div>
                  <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                    {t('common.company')}
                  </dt>
                  <dd className="mt-1">{profile.customerName ?? profile.customerId}</dd>
                </div>
              )}
              {profile.maxDiscountPercent !== null && (
                <div>
                  <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                    {t('profile.account.discountCeiling')}
                  </dt>
                  <dd className="mt-1">{profile.maxDiscountPercent}%</dd>
                </div>
              )}
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                  {t('profile.account.accountCreated')}
                </dt>
                <dd className="mt-1">{day(profile.createdAt)}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                  {t('profile.account.lastSignIn')}
                </dt>
                <dd className="mt-1">{dt(profile.lastSignInAt)}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        {/* ------------------------------------------------- Language + appearance
            Two personal settings, deliberately in one column and deliberately worded
            differently: the theme is a per-browser comfort setting, the language is a property of
            the account and travels with it (users.locale, migration 091). */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Languages className="h-4 w-4" />
                {t('profile.language.title')}
              </CardTitle>
              <CardDescription>{t('profile.language.description')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="inline-flex w-full divide-x divide-border overflow-hidden rounded-md border border-input">
                {LOCALES.map((value) => (
                  <button
                    key={value}
                    type="button"
                    disabled={savingLocale}
                    onClick={() => void switchLanguage(value)}
                    aria-pressed={locale === value}
                    className={cn(
                      'flex flex-1 items-center justify-center gap-1.5 px-2 py-2 text-xs transition-colors disabled:opacity-60',
                      locale === value
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                    )}
                  >
                    {LOCALE_LABELS[value]}
                  </button>
                ))}
              </div>
              {/* Said on the screen, not just in the code: an operator switching to English must
                  not expect an English invoice to come out of the printer. */}
              <p className="text-xs text-muted-foreground">
                {t('profile.language.documentsNotice')}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('profile.appearance.title')}</CardTitle>
              <CardDescription>{t('profile.appearance.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="inline-flex w-full divide-x divide-border overflow-hidden rounded-md border border-input">
                {THEMES.map(({ value, icon: Icon }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setTheme(value)}
                    aria-pressed={theme === value}
                    className={cn(
                      'flex flex-1 items-center justify-center gap-1.5 px-2 py-2 text-xs transition-colors',
                      theme === value
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {t(`profile.appearance.${value}`)}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* -------------------------------------------------------------- Password */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>{t('profile.password.title')}</CardTitle>
            <CardDescription>{t('profile.password.description')}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={changePassword} className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="current-pwd">{t('profile.password.current')}</Label>
                <Input
                  id="current-pwd"
                  className="max-w-sm"
                  type={reveal ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="new-pwd">{t('profile.password.new')}</Label>
                <div className="relative">
                  <Input
                    id="new-pwd"
                    type={reveal ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={next}
                    onChange={(e) => setNext(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setReveal((v) => !v)}
                    aria-label={reveal ? t('profile.password.hide') : t('profile.password.show')}
                    className="absolute inset-y-0 right-0 grid w-9 place-items-center text-muted-foreground hover:text-foreground"
                  >
                    {reveal ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm-pwd">{t('profile.password.confirm')}</Label>
                <Input
                  id="confirm-pwd"
                  type={reveal ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={confirmPwd}
                  onChange={(e) => setConfirmPwd(e.target.value)}
                  aria-invalid={mismatch}
                  className={mismatch ? 'border-destructive' : undefined}
                />
                {mismatch && (
                  <p className="text-xs text-destructive">{t('profile.password.mismatch')}</p>
                )}
              </div>

              <ul className="grid gap-1.5 text-xs sm:col-span-2 sm:grid-cols-2">
                {RULES.map((r, i) => (
                  <li
                    key={r.key}
                    className={cn(
                      'flex items-center gap-1.5',
                      next.length === 0
                        ? 'text-muted-foreground'
                        : rulesMet[i]
                          ? 'text-success'
                          : 'text-muted-foreground'
                    )}
                  >
                    {next.length > 0 && rulesMet[i] ? (
                      <Check className="h-3.5 w-3.5" />
                    ) : (
                      <X className="h-3.5 w-3.5 opacity-40" />
                    )}
                    {t(`profile.password.rules.${r.key}`)}
                  </li>
                ))}
              </ul>

              <div className="sm:col-span-2">
                <Button type="submit" disabled={!canSubmitPassword || changing}>
                  <KeyRound className="h-4 w-4" />
                  {changing ? t('profile.password.submitting') : t('profile.password.submit')}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* ------------------------------------------------------ Sign-in activity
            Shares a grid row with the Password card, so `align-items: stretch` already makes the
            two the same height — provided this card never becomes the taller one. That is what the
            absolutely-positioned scroll area below is for: an out-of-flow child contributes
            nothing to the row's max-content height, so however long the list gets, the row stays
            sized by the Password card and the overflow scrolls instead. A plain `overflow-y-auto`
            would NOT do: an `auto` grid row still grows to its items' max-content height, so the
            list would push both cards taller and never scroll. */}
        <Card className="flex flex-col">
          <CardHeader>
            <CardTitle>{t('profile.activity.title')}</CardTitle>
            <CardDescription>{t('profile.activity.description')}</CardDescription>
          </CardHeader>
          {/* min-h-56 is the single-column fallback: stacked on mobile there is no sibling to
              stretch against, and a flex-1 parent of an absolute child would collapse to zero. */}
          <CardContent className="relative min-h-56 flex-1 p-0">
            <div className="absolute inset-0 overflow-y-auto px-6 pb-6">
              {signIns.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('profile.activity.empty')}</p>
              ) : (
                <ul className="space-y-2.5 text-sm">
                  {signIns.map((s, i) => (
                    <li key={`${s.at}-${i}`} className="flex items-start gap-2">
                      {s.success ? (
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                      ) : (
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
                      )}
                      <div className="min-w-0">
                        <div className="truncate">{dt(s.at)}</div>
                        <div className="text-xs text-muted-foreground">
                          {s.success
                            ? t('profile.activity.signedIn')
                            : t('profile.activity.failed')}
                          {s.ip ? ` · ${s.ip}` : ''}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </CardContent>
        </Card>

        {/* ------------------------------------------------------------- Sessions */}
        <Card className="lg:col-span-3">
          <CardHeader className="flex-row items-start justify-between space-y-0">
            <div className="space-y-1.5">
              <CardTitle>{t('profile.devices.title')}</CardTitle>
              <CardDescription>{t('profile.devices.description')}</CardDescription>
            </div>
            {otherSessions > 0 && (
              <Button
                size="sm"
                variant="outline"
                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                disabled={revoking !== null}
                onClick={revokeOthers}
              >
                <LogOut className="h-3.5 w-3.5" />
                {t('profile.devices.signOutOthers')}
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {sessions.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('profile.devices.empty')}</p>
            ) : (
              <>
                <ul className="divide-y">
                  {pageSessions.map((s) => {
                    const { label, kind } = describeDevice(s.userAgent, t);
                    const isCurrent = s.jti === currentJti;
                    const Icon = kind === 'mobile' ? Smartphone : Laptop;
                    return (
                      <li key={s.jti} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="truncate text-sm font-medium">{label}</span>
                            {isCurrent && (
                              <Badge variant="success">{t('profile.devices.thisDevice')}</Badge>
                            )}
                          </div>
                          <div className="truncate text-xs text-muted-foreground">
                            {s.ip ? `${s.ip} · ` : ''}
                            {t('profile.devices.meta', {
                              issued: dt(s.issuedAt),
                              expires: dt(s.expiresAt),
                            })}
                          </div>
                        </div>
                        {!isCurrent && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="shrink-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                            disabled={revoking !== null}
                            onClick={() => revokeOne(s)}
                          >
                            {revoking === s.jti
                              ? t('profile.devices.signingOut')
                              : t('profile.devices.signOut')}
                          </Button>
                        )}
                      </li>
                    );
                  })}
                </ul>
                {/* Only a paged list needs a pager: with five or fewer devices the controls would
                  be a permanent "Page 1 / 1" with both buttons dead. */}
                {totalPages > 1 && (
                  <div className="mt-3 flex items-center justify-between border-t pt-3 text-xs text-muted-foreground">
                    <div>
                      {t('profile.devices.range', {
                        from: sessionPage * SESSIONS_PER_PAGE + 1,
                        to: sessionPage * SESSIONS_PER_PAGE + pageSessions.length,
                        total: orderedSessions.length,
                      })}
                    </div>
                    <div className="flex items-center gap-2">
                      <span>{t('ui.pageOf', { page: sessionPage + 1, total: totalPages })}</span>
                      <div className="flex gap-1">
                        <button
                          type="button"
                          aria-label={t('profile.devices.prevPage')}
                          disabled={sessionPage === 0}
                          onClick={() => setSessionPage((p) => Math.max(0, p - 1))}
                          className="h-7 rounded-md border border-input bg-background px-2 transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-40"
                        >
                          {t('ui.prev')}
                        </button>
                        <button
                          type="button"
                          aria-label={t('profile.devices.nextPage')}
                          disabled={sessionPage + 1 >= totalPages}
                          onClick={() => setSessionPage((p) => Math.min(totalPages - 1, p + 1))}
                          className="h-7 rounded-md border border-input bg-background px-2 transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-40"
                        >
                          {t('ui.next')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* ---------------------------------------------------------- Capabilities */}
        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle>{t('profile.capabilities.title')}</CardTitle>
            <CardDescription>{t('profile.capabilities.description')}</CardDescription>
          </CardHeader>
          <CardContent>
            {!caps ? (
              <Skeleton className="h-24 w-full" />
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {[...(caps.actions ?? caps.permissions ?? [])].sort().map((a) => (
                  <Badge key={a} variant="muted" className="font-mono text-[11px]">
                    {a}
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {confirmDialog}
    </div>
  );
}
